import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addDays, addWeeks, format, isSameDay, startOfWeek } from 'date-fns'
import { toast } from 'sonner'
import { ChevronLeft, ChevronRight, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react'
import { api, type SocialPlatform, type SocialPost, type SocialStatus } from '@/lib/api'
import { cn } from '@/lib/utils'

const PLATFORMS: Array<{ key: SocialPlatform; label: string; icon: typeof Twitter }> = [
  { key: 'x',         label: 'X',         icon: Twitter },
  { key: 'linkedin',  label: 'LinkedIn',  icon: Linkedin },
  { key: 'facebook',  label: 'Facebook',  icon: Facebook },
  { key: 'instagram', label: 'Instagram', icon: Instagram },
]

const STATUS_CLASS: Record<SocialStatus, string> = {
  draft:     'bg-status-draft-bg border-l-status-draft-accent text-status-draft-fg',
  in_review: 'bg-status-review-bg border-l-status-review-accent text-status-review-fg',
  approved:  'bg-status-approved-bg border-l-status-approved-accent text-status-approved-fg',
  scheduled: 'bg-brand-50 border-l-brand-400 text-brand-800',
  published: 'bg-status-published-bg border-l-status-published-accent text-status-published-fg',
}

export function SocialPage() {
  const qc = useQueryClient()
  const [weekOffset, setWeekOffset] = useState(0)
  const [open, setOpen] = useState<SocialPost | null>(null)

  const { data: posts = [] } = useQuery({ queryKey: ['social.posts'], queryFn: api.socialPosts })

  const weekStart = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 })
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  const approve = useMutation({
    mutationFn: (id: string) => api.approveSocial(id),
    onSuccess: () => {
      toast.success('Approved')
      qc.invalidateQueries({ queryKey: ['social.posts'] })
      setOpen(null)
    },
  })
  const schedule = useMutation({
    mutationFn: ({ id, when }: { id: string; when: string }) => api.scheduleSocial(id, when),
    onSuccess: () => {
      toast.success('Scheduled')
      qc.invalidateQueries({ queryKey: ['social.posts'] })
      setOpen(null)
    },
  })

  const byCell = (platform: SocialPlatform, day: Date) =>
    posts.filter(
      (p) =>
        p.platform === platform &&
        p.scheduled_for &&
        isSameDay(new Date(p.scheduled_for), day),
    )

  const unscheduled = posts.filter((p) => !p.scheduled_for)

  return (
    <div className="p-3 md:p-5 max-w-[1400px] mx-auto flex flex-col gap-3.5">
      <header className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
        <div>
          <h1 className="text-xl font-medium">Social scheduling</h1>
          <p className="text-xs text-ink-secondary mt-0.5">
            Per-platform calendar across X, LinkedIn, Facebook, Instagram. Click a card to approve or reschedule.
          </p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setWeekOffset((w) => w - 1)}
            className="p-1.5 border border-border rounded-md hover:bg-surface-muted"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className={cn(
              'text-xs px-2.5 py-1.5 border border-border rounded-md hover:bg-surface-muted',
              weekOffset === 0 && 'bg-surface-muted',
            )}
          >
            This week
          </button>
          <button
            onClick={() => setWeekOffset((w) => w + 1)}
            className="p-1.5 border border-border rounded-md hover:bg-surface-muted"
          >
            <ChevronRight size={16} />
          </button>
          <span className="text-xs text-ink-tertiary ml-2">
            Week of {format(weekStart, 'MMM d')}
          </span>
        </div>
      </header>

      <section className="border border-border rounded-md bg-surface-card overflow-x-auto">
        <div className="grid grid-cols-[80px_repeat(7,minmax(120px,1fr))] border-b border-border bg-surface-muted/50 min-w-[880px]">
          <div className="px-2 py-2 text-[11px] text-ink-tertiary">Platform</div>
          {days.map((d) => (
            <div
              key={d.toISOString()}
              className={cn(
                'px-1.5 py-2 text-[11px] border-l border-border flex flex-col',
                isSameDay(d, new Date()) ? 'text-brand-600' : 'text-ink-tertiary',
              )}
            >
              <span>{format(d, 'EEE')}</span>
              <span className="text-sm text-ink-primary tabular-nums">{format(d, 'd')}</span>
            </div>
          ))}
        </div>

        {PLATFORMS.map(({ key, label, icon: Icon }) => (
          <div
            key={key}
            className="grid grid-cols-[80px_repeat(7,minmax(120px,1fr))] border-b border-border last:border-0 min-w-[880px]"
          >
            <div className="px-2 py-2 flex items-center gap-1.5 text-xs text-ink-secondary bg-surface-muted/30">
              <Icon size={13} /> {label}
            </div>
            {days.map((d) => {
              const cell = byCell(key, d)
              return (
                <div
                  key={d.toISOString()}
                  className="border-l border-border min-h-[72px] p-1 flex flex-col gap-1"
                >
                  {cell.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setOpen(p)}
                      className={cn(
                        'text-left rounded border-l-2 px-1.5 py-1 text-[11px] leading-tight hover:opacity-90',
                        STATUS_CLASS[p.status],
                      )}
                    >
                      <div className="truncate">{p.body.slice(0, 80)}</div>
                      <div className="opacity-80 text-[10px] mt-0.5 truncate">
                        {p.status.replace('_', ' ')}
                        {p.scheduled_for && ` · ${format(new Date(p.scheduled_for), 'h:mma')}`}
                      </div>
                    </button>
                  ))}
                </div>
              )
            })}
          </div>
        ))}
      </section>

      {unscheduled.length > 0 && (
        <section className="border border-border rounded-md bg-surface-card p-3">
          <h2 className="text-sm font-medium mb-2">Unscheduled · {unscheduled.length}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {unscheduled.map((p) => (
              <button
                key={p.id}
                onClick={() => setOpen(p)}
                className={cn(
                  'text-left rounded border border-border px-2 py-1.5 text-xs hover:bg-surface-muted',
                )}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-ink-tertiary">
                    {p.platform}
                  </span>
                  <span className={cn('text-[10px] px-1.5 rounded', STATUS_CLASS[p.status])}>
                    {p.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="text-xs mt-1 truncate">{p.body}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
          onClick={() => setOpen(null)}
        >
          <div
            className="bg-surface-card rounded-lg border border-border max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border flex justify-between items-start">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">
                  {open.platform}
                </div>
                <div className="text-sm font-medium mt-0.5">
                  {open.article_title ?? 'Standalone post'}
                </div>
              </div>
              <span className={cn('text-[10px] px-1.5 py-0.5 rounded', STATUS_CLASS[open.status])}>
                {open.status.replace('_', ' ')}
              </span>
            </div>
            <div className="p-4 text-sm whitespace-pre-wrap">{open.body}</div>
            <div className="px-4 pb-4 flex gap-2 justify-end">
              <button
                onClick={() => setOpen(null)}
                className="text-xs border border-border rounded-md px-3 py-1.5 hover:bg-surface-muted"
              >
                Close
              </button>
              {open.status !== 'approved' && open.status !== 'scheduled' && (
                <button
                  disabled={approve.isPending}
                  onClick={() => approve.mutate(open.id)}
                  className="text-xs bg-status-approved-accent text-white rounded-md px-3 py-1.5 hover:opacity-90 disabled:opacity-60"
                >
                  {approve.isPending ? 'Approving…' : 'Approve'}
                </button>
              )}
              {open.status === 'approved' && (
                <button
                  disabled={schedule.isPending}
                  onClick={() => {
                    const when = new Date()
                    when.setHours(when.getHours() + 24)
                    schedule.mutate({ id: open.id, when: when.toISOString() })
                  }}
                  className="text-xs bg-brand-600 text-white rounded-md px-3 py-1.5 hover:opacity-90 disabled:opacity-60"
                >
                  {schedule.isPending ? 'Scheduling…' : 'Schedule +24h'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
